import { useMemo } from 'react';
import { useDataFetch } from '../../../Hooks/Use-data-fetch';

const useOrderOverview = (selectedCountry, groupBy = 'month', province, city) => {
  const { getData } = useDataFetch();

  const { data, isLoading, error } = getData(
    ['growth_metrics', selectedCountry, groupBy, province, city],
    '/charts/growth-metrics',
    { enabled: !!selectedCountry },
    {
      countryId: selectedCountry,
      groupBy,
      province,
      city
    }
  );

  const processedData = useMemo(() => {
    if (!data?.data) {
      return {
        totalSales: 0,
        monthlySales: [],
        chartConfig: getEmptyChartConfig()
      };
    }

    const totalSales = data.data.totalSalesCount?.total ?? 0;

    const monthlySales = Array.isArray(
      data.data.totalSalesCount?.groups
    )
      ? data.data.totalSalesCount.groups.filter(
          item => item && item.label
        )
      : [];

    const chartConfig = generateChartConfig(monthlySales);

    return {
      totalSales,
      monthlySales,
      chartConfig
    };
  }, [data]);

  return {
    ...processedData,
    isLoading,
    error
  };
};

const getEmptyChartConfig = () => ({
  series: [
    {
      name: 'Ventas',
      type: 'area',
      data: [0]
    }
  ],

  options: {
    chart: {
      height: 350,
      type: 'area',
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      },
      fontFamily: 'Rubik, sans-serif',
      dropShadow: {
        enabled: true,
        top: 2,
        left: 0,
        blur: 4,
        color: '#000',
        opacity: 0.08
      }
    },

    colors: ['#F59E0B'],

    stroke: {
      width: 3,
      curve: 'smooth'
    },

    dataLabels: {
      enabled: false
    },

    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.3,
        inverseColors: false,
        opacityFrom: 0.45,
        opacityTo: 0.02,
        stops: [0, 100]
      }
    },

    markers: {
      size: 4,
      colors: ['#F59E0B'],
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: {
        size: 7
      }
    },

    grid: {
      borderColor: '#ECECEC',
      strokeDashArray: 4,
      padding: {
        left: 5,
        right: 10,
        top: 15
      }
    },

    xaxis: {
      categories: ['Sin datos'],

      axisBorder: {
        show: false
      },

      axisTicks: {
        show: false
      },

      labels: {
        style: {
          colors: '#7E8299',
          fontSize: '12px'
        }
      }
    },

    yaxis: {
      min: 0,
      max: 10,
      tickAmount: 4,

      labels: {
        formatter: value =>
          Number(value).toLocaleString(),

        style: {
          colors: '#7E8299'
        }
      }
    },

    tooltip: {
      theme: 'light',
      shared: true,
      intersect: false,

      marker: {
        show: true
      },

      x: {
        show: true
      },

      y: {
        formatter: value =>
          `${Number(value).toLocaleString()} ventas`
      }
    },

    legend: {
      show: false
    },

    responsive: [
      {
        breakpoint: 1200,
        options: {
          chart: {
            height: 280
          }
        }
      },

      {
        breakpoint: 768,
        options: {
          chart: {
            height: 240
          }
        }
      }
    ]
  }
});

const generateChartConfig = (monthlySales = []) => {
  const categories = monthlySales.map(
    item => item.label
  );

  const salesData = monthlySales.map(
    item => Number(item.total) || 0
  );

  const maxSales = Math.max(...salesData, 0);

  const maxValue =
    maxSales === 0
      ? 10
      : Math.ceil(maxSales * 1.2);

  return {
    series: [
      {
        name: 'Ventas',
        type: 'area',
        data: salesData
      }
    ],

    options: {
      chart: {
        id: 'sales-growth',
        height: 350,
        type: 'area',
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        },
        fontFamily: 'Rubik, sans-serif'
      },

      colors: ['#F59E0B'],

      stroke: {
        curve: 'smooth',
        width: 3
      },

      dataLabels: {
        enabled: false
      },

      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.3,
          inverseColors: false,
          opacityFrom: 0.45,
          opacityTo: 0.02,
          stops: [0, 100]
        }
      },

      markers: {
        size: 4,
        colors: ['#F59E0B'],
        strokeColors: '#fff',
        strokeWidth: 2,

        hover: {
          size: 7
        }
      },

      grid: {
        borderColor: '#ECECEC',
        strokeDashArray: 4,

        padding: {
          left: 5,
          right: 10,
          top: 15
        }
      },

      xaxis: {
        categories,

        axisBorder: {
          show: false
        },

        axisTicks: {
          show: false
        },

        labels: {
          style: {
            colors: '#7E8299',
            fontSize: '12px'
          }
        }
      },

      yaxis: {
        min: 0,
        max: maxValue,
        tickAmount: 4,

        labels: {
          formatter: value =>
            Number(value).toLocaleString(),

          style: {
            colors: '#7E8299'
          }
        }
      },

      tooltip: {
        theme: 'light',
        shared: true,
        intersect: false,

        marker: {
          show: true
        },

        x: {
          show: true
        },

        y: {
          formatter: value =>
            `${Number(value).toLocaleString()} ventas`
        }
      },

      legend: {
        show: false
      },

      responsive: [
        {
          breakpoint: 1200,
          options: {
            chart: {
              height: 280
            }
          }
        },

        {
          breakpoint: 768,
          options: {
            chart: {
              height: 240
            }
          }
        }
      ]
    }
  };
};

export default useOrderOverview;